import { type DependencyList, useCallback, useEffect, useRef, useState } from "react";

export type Resource<T> =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; data: T };

export interface ResourceHandle<T> {
  resource: Resource<T>;
  /** Loads again. The current data stays on screen until the new data arrives. */
  reload: () => Promise<void>;
  /** Replaces the data with a value the caller already has, such as a mutation's answer. */
  replace: (data: T) => void;
}

export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Something went wrong.";
}

/** Loads data for a screen and keeps the latest answer; answers to stale loads are dropped. */
export function useResource<T>(load: () => Promise<T>, deps: DependencyList): ResourceHandle<T> {
  const [resource, setResource] = useState<Resource<T>>({ status: "loading" });
  const generation = useRef(0);
  // The caller's deps decide when `load` changes, as with useEffect.
  const stableLoad = useCallback(load, deps);

  const run = useCallback(async () => {
    generation.current += 1;
    const mine = generation.current;
    try {
      const data = await stableLoad();
      if (mine === generation.current) setResource({ status: "ready", data });
    } catch (error) {
      if (mine === generation.current) setResource({ status: "error", message: errorMessage(error) });
    }
  }, [stableLoad]);

  useEffect(() => {
    setResource({ status: "loading" });
    void run();
    return () => {
      generation.current += 1;
    };
  }, [run]);

  const replace = useCallback((data: T) => {
    generation.current += 1;
    setResource({ status: "ready", data });
  }, []);

  return { resource, reload: run, replace };
}
